var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

/* eslint-disable default-case */
import { types, onSnapshot, getEnv, destroy } from "mobx-state-tree";
import { startsWith, isEmpty, flatten } from "lodash-es";
import { generateId, getNewFormulaName, getNewTableName, createAndFillGrid, prependWithFormulaSign } from "../core/utils";
import { Parser, rename } from "../core/parser";
import { SUPPORTED_FUNCTIONS } from "../core/functions";
import { NODE_TYPES } from "../core/common";
import { FormulaModel } from "./Formula";
import { TableModel, ColumnModel } from "./Table";
import { ChartModel } from "./Chart";
import { DataSourceModel, DataSourceProviderModel } from "./DataSource";

export var DEFAULT_ROW_COUNT = 4;

export function ValidationError(field, code) {
  this.field = field;
  this.code = code;
}

var NodeModelsUnion = types.union({
  dispatcher: function dispatcher(_ref) {
    var type = _ref.type;

    switch (type) {
      case NODE_TYPES.formula:
        {
          return FormulaModel;
        }
      case NODE_TYPES.table:
        {
          return TableModel;
        }
      default:
        {
          return ChartModel;
        }
    }
  }
}, FormulaModel, TableModel, ChartModel);

var Store = types.model("StoreModel", {
  id: types.optional(types.string, function () {
    return generateId();
  }),
  nodes: types.array(NodeModelsUnion),
  dataSources: types.array(DataSourceModel),
  dataSourceProvider: types.maybeNull(DataSourceProviderModel),
  readOnly: false
}).views(function (self) {
  return {
    get formulas() {
      return self.nodes.filter(function (_ref2) {
        var type = _ref2.type;
        return type === NODE_TYPES.formula;
      });
    },
    get tables() {
      return self.nodes.filter(function (_ref3) {
        var type = _ref3.type;
        return type === NODE_TYPES.table;
      });
    },
    get charts() {
      return self.nodes.filter(function (_ref4) {
        var type = _ref4.type;
        return type === NODE_TYPES.chart;
      });
    },
    get dataSourceFields() {
      return flatten(self.dataSources.map(function (_ref5) {
        var fields = _ref5.fields;
        return fields;
      }));
    },
    get isEmpty() {
      return self.nodes.length === 0;
    },
    // ToDo: change to "editingFormula"
    get editingNode() {
      return self.formulas.find(function (_ref6) {
        var isEditing = _ref6.isEditing;
        return isEditing;
      });
    },
    // ToDo: change to "isSomeFormulaEditing"
    get isSomeNodeEditing() {
      return !!self.editingNode;
    },
    get functions() {
      var functionsFromEnv = getEnv(self).functions;

      if (!functionsFromEnv || isEmpty(functionsFromEnv)) {
        return SUPPORTED_FUNCTIONS;
      }

      return SUPPORTED_FUNCTIONS.filter(function (_ref7) {
        var name = _ref7.name;
        return functionsFromEnv.some(function (fn) {
          return fn.toUpperCase() === name.toUpperCase();
        });
      });
    },
    findById: function findById(id) {
      return self.nodes.find(function (n) {
        return n.id === id;
      });
    },
    findByName: function findByName(name) {
      return self.nodes.find(function (n) {
        return n.name === name;
      });
    },
    getData: function getData() {
      var path = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];

      if (path.length === 0) {
        return null;
      }

      var node = self.findById(path[0]);

      if (!node) {
        return null;
      }

      if (node.type === NODE_TYPES.formula) {
        return {
          id: node.id,
          name: node.name,
          data: node.parsed.result
        };
      }

      if (node.type === NODE_TYPES.table) {
        if (path[1]) {
          var col = node.getColumnById(path[1]);

          if (!col) {
            return null;
          }

          return {
            id: col.id,
            name: col.name,
            data: col.getColumnCells().map(function (_ref8) {
              var parsed = _ref8.parsed;
              return parsed.result;
            })
          };
        }

        return node.serialize();
      }

      return null;
    },

    onNodesSnapshot: function onNodesSnapshot(callback) {
      return onSnapshot(self.nodes, callback);
    }
  };
}).volatile(function () {
  var parser = new Parser();

  return {
    parser: parser
  };
}).actions(function (self) {
  return {
    createAndAddFormula: function createAndAddFormula() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var _props$name = props.name,
          name = _props$name === undefined ? getNewFormulaName(self.nodes) : _props$name,
          rest = _objectWithoutProperties(props, ["name"]);

      var formula = FormulaModel.create(_extends({}, rest, {
        name: name
      }));
      self.nodes.push(formula);

      return formula;
    },
    createAndAddTable: function createAndAddTable() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var _props$name2 = props.name,
          name = _props$name2 === undefined ? getNewTableName(self.nodes, getEnv(self).tableNamePrefix) : _props$name2,
          _cols = props.columns,
          _rows = props.rows,
          rest = _objectWithoutProperties(props, ["name", "columns", "rows"]);

      var columns = (_cols || [{ name: "Column1" }, { name: "Column2" }, { name: "Column3" }]).map(function (c) {
        return ColumnModel.create(c);
      });

      var rows = _rows || createAndFillGrid(DEFAULT_ROW_COUNT, columns);

      var table = TableModel.create(_extends({}, rest, {
        name: name,
        columns: columns,
        rows: rows
      }));
      self.nodes.push(table);

      return table;
    },
    createAndAddChart: function createAndAddChart() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      var chart = ChartModel.create(props);
      self.nodes.push(chart);

      return chart;
    },
    createAndAddDataSource: function createAndAddDataSource(props) {
      var dataSource = DataSourceModel.create(props);
      self.dataSources.push(dataSource);

      return dataSource;
    },
    createAndAddNode: function createAndAddNode(type, props) {
      switch (type) {
        case NODE_TYPES.formula:
          {
            return self.createAndAddFormula(props);
          }
        case NODE_TYPES.table:
          {
            return self.createAndAddTable(props);
          }
        case NODE_TYPES.chart:
          {
            return self.createAndAddChart(props);
          }
        default:
          return null;
      }
    },
    removeNode: function removeNode(node) {
      destroy(node);
    },
    renameNode: function renameNode(node, oldName, newName) {
      self.nodes.forEach(function (n) {
        if (node.type === NODE_TYPES.formula && node.id === n.id) {
          // Skip formula that is triggering rename (it has already been renamed)
          return;
        }

        switch (n.type) {
          case NODE_TYPES.table:
            {
              n.columns.forEach(function (col) {
                if (col.isColumnValueFormula) {
                  var renamed = rename(col.columnValue, node.type, oldName, newName);
                  col.setColumnValue(prependWithFormulaSign(renamed));
                }

                if (col.isColumnFooterFormula) {
                  var _renamed = rename(col.footerValue, node.type, oldName, newName);
                  col.setFooterValue(prependWithFormulaSign(_renamed));
                }
              });
              break;
            }
          case NODE_TYPES.formula:
            {
              if (n.isFormula) {
                var renamed = rename(n.value, node.type, oldName, newName);
                n.setValue(prependWithFormulaSign(renamed));
              }
              break;
            }
        }
      });
    },
    renameColumn: function renameColumn(table, _column, oldName, newName) {
      self.nodes.forEach(function (node) {
        switch (node.type) {
          case NODE_TYPES.table:
            {
              var columns = node.columns;


              columns.forEach(function (col) {
                if (col.id === _column.id && col.isColumnFooterFormula) {
                  // if it's the same column, update just footer

                  var renamed = rename(col.footerValue, NODE_TYPES.column, oldName, newName, table.name);
                  col.setFooterValue(startsWith(col.footerValue, "=") ? prependWithFormulaSign(renamed) : renamed);
                  return;
                }

                if (col.isColumnValueFormula) {
                  var _renamed2 = rename(col.columnValue, NODE_TYPES.column, oldName, newName, table.name);
                  col.setColumnValue(prependWithFormulaSign(_renamed2));
                }
              });

              break;
            }
          case NODE_TYPES.formula:
            {
              var renamed = rename(node.value, NODE_TYPES.column, oldName, newName, table.name);
              node.setValue(prependWithFormulaSign(renamed));
              break;
            }
        }
      });
    },
    setReadOnly: function setReadOnly() {
      var readOnly = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !self.readOnly;

      self.readOnly = readOnly;
    }
  };
});
export { Store };